import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  PlayArrow,
  Message,
  FlashOn,
  AccountTree,
  Add,
  Assignment,
} from "@mui/icons-material";
import { ColorsConstants } from "../../../cssUtils/constants";
import { useAuth } from "../../../hooks/AuthProvider";
import { NODEBOT_NODE_TYPES } from "../../../config/urls";

const nodeIcons = {
  start: PlayArrow,
  message: Message,
  flow_message: Assignment,
  trigger: FlashOn,
  logic: AccountTree,
};

const categoryColors = {
  start: "#4caf50",
  message: "#2196f3",
  flow_message: "#9c27b0",
  trigger: "#ff9800",
  logic: "#f44336",
};

const NodePalette = ({ onAddNode }) => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();

  const [nodeTypes, setNodeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupedNodes, setGroupedNodes] = useState({});

  useEffect(() => {
    loadNodeTypes();
  }, []);

  const loadNodeTypes = async () => {
    try {
      const token = await getValidToken();

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const response = await fetch(NODEBOT_NODE_TYPES, { headers });

      if (!response.ok) {
        throw new Error("Failed to load node types");
      }

      const types = (await response.json()).results;

      setNodeTypes(types);

      // Group by category
      const grouped = types.reduce((acc, type) => {
        if (!acc[type.category]) {
          acc[type.category] = [];
        }
        acc[type.category].push(type);
        return acc;
      }, {});

      setGroupedNodes(grouped);
    } catch (error) {
      console.error("Failed to load node types:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (event, nodeType) => {
    event.dataTransfer.setData(
      "application/reactflow",
      JSON.stringify(nodeType),
    );
    event.dataTransfer.effectAllowed = "move";
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 280,
        borderRight: "1px solid",
        borderColor: ColorsConstants.primaryGray10,
        height: "100%",
        overflowY: "auto",
        backgroundColor: "#fafafa",
      }}
    >
      <Box
        sx={{
          p: 2,
          backgroundColor: "white",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
          Node Palette
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: ColorsConstants.primaryGray60 }}
        >
          Drag nodes to the canvas
        </Typography>
      </Box>

      {Object.entries(groupedNodes).map(([category, types]) => (
        <Box key={category} sx={{ mb: 2 }}>
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Chip
              label={category.replace("_", " ").toUpperCase()}
              size="small"
              sx={{
                backgroundColor: categoryColors[category] || "#757575",
                color: "white",
                fontWeight: "bold",
                fontSize: "0.7rem",
              }}
            />
          </Box>

          <List dense>
            {types.map((nodeType) => {
              const Icon = nodeIcons[nodeType.key] || Message;

              return (
                <ListItem
                  key={nodeType.key}
                  draggable
                  onDragStart={(e) => handleDragStart(e, nodeType)}
                  sx={{
                    cursor: "grab",
                    backgroundColor: "white",
                    mb: 0.5,
                    mx: 1,
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      boxShadow: 1,
                    },
                    "&:active": {
                      cursor: "grabbing",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Icon
                      sx={{
                        color: categoryColors[nodeType.category] || "#757575",
                        fontSize: 20,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={nodeType.name}
                    secondary={nodeType.description?.substring(0, 50) + "..."}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: "medium",
                    }}
                    secondaryTypographyProps={{
                      variant: "caption",
                      sx: { fontSize: "0.65rem" },
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      ))}
    </Box>
  );
};

export default NodePalette;
